<script setup>
import {computed, inject, ref} from "vue";
import {useCommunityPublicInformationStore} from "@/stores/communityPublicInformationStore";
import {useI18n} from 'vue-i18n';
import {useFrontEndStore} from "@/stores/frontEndStore";
import router from "@/router/routerSetup";
import { useApplicantStore } from "@/stores/applicationStore";
import { DialogConfirm, PrimaryButton, useSnackbarStore } from '@es-bill/es-bill-common-components';

const $log = inject('log');
const {t} = useI18n();
const communityStore = useCommunityPublicInformationStore();
const applicationStore = useApplicantStore();
const frontEndStore = useFrontEndStore();
const snackbarStore = useSnackbarStore();

const sortBy = [{key: 'name', order: 'asc'}];

const tableHeader = computed(() => [
  {title: t('name'), value: 'name'},
  {title: 'ECN', value: 'ecn'},
  {title: 'ECID', value: 'ecid'},
  {title: t('communityType'), value: 'communityType'},
  {title: t('dso'), value: 'dso'},
  {title: t('actions'), value: 'actions', sortable: false},
]);

// Ensure filter object exists to avoid runtime errors
if (!frontEndStore.communityPublicFilter) {
  frontEndStore.communityPublicFilter = { ecnList: [] };
}

communityStore.fetchCommunities();

const communitiesAvailable = computed(() => communityStore.communities.map(c => ({...c, props: {subtitle: c.ecn }})));

const communityList = computed(() => {
  let communities = communityStore.communities;

  // Apply community filter if implemented
  if (frontEndStore.communityPublicFilter?.ecnList?.length) {
    communities = communities.filter(community => frontEndStore.communityPublicFilter.ecnList.includes(community.ecn));
  }

  return communities;
});

const isDeleteDialogVisible = ref(false);
const selectedEcn = ref(null); // To store the selected ECN for deletion

function showDeleteDialog(ecn) {
  const applications = applicationStore.getApplicationByEcn(ecn);
  if (applications.length > 0) {
    snackbarStore.showSnackbar(t('communityInUseCannotBeDeleted'), '', 'Error', 10000, []);
    return;
  }

  selectedEcn.value = ecn;
  isDeleteDialogVisible.value = true;
}

async function handleDeleteConfirm() {
  if (selectedEcn.value) {
    try {
      await communityStore.deleteCommunity(selectedEcn.value);
      snackbarStore.showSnackbar(t('applicantMessages.deletedSuccess'), '', 'Success', 4000, []);
    } catch (error) {
      snackbarStore.showSnackbar(error?.response?.data?.message || t('unexpectedError'), '', 'Error', 10000, []);
    }
    selectedEcn.value = null;
  }
  isDeleteDialogVisible.value = false;
}

function handleDeleteCancel() {
  selectedEcn.value = null; // Reset the selected ECN
  isDeleteDialogVisible.value = false;
}
</script>

<template>
  <v-container class="pa-0">
    <div class="d-flex align-center mb-4">
      <p class="textHeadline flex-grow-1">{{ $t('publicCommunityInformation') }}</p>
    </div>

    <div class="d-flex flex-column flex-md-row w-100 mb-3">
        <v-autocomplete
          class="w-100"
          bg-color="white"
          :label="$t('filter') + ' ' + $t('publicCommunityInformation')"
          v-model="frontEndStore.communityPublicFilter.ecnList"
          :items="communitiesAvailable"
          :filter-keys="['title','props.subtitle']"
          multiple
          variant="outlined"
          item-value="ecn"
          item-title="name"
          clearable
          hide-details
        ></v-autocomplete>
        <PrimaryButton prepend-icon="mdi-plus" :text="t('createCommunity')" @click="router.push({ name: 'communityForm' })"/>
      </div>


      <v-data-table
        class="elevation-2 mainViewContent"
        :sort-by="sortBy"
        :headers="tableHeader"
        :items="communityList">

        <template v-slot:[`item.actions`]="{ item }">
          <v-icon
            @click="showDeleteDialog(item.ecn)"
            class="mr-2"
          >mdi-delete
          </v-icon>
          <v-icon
            @click="router.push({ name: 'communityForm', params: { id: item.ecn }})"
          >mdi-pencil
          </v-icon>
        </template>
      </v-data-table>

    <!-- Include the DialogConfirm common component -->
    <DialogConfirm
      :modelValue="isDeleteDialogVisible"
      @update:modelValue="isDeleteDialogVisible = $event"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
      :title="t('areYouSure')"
      :message="t('deleteConfirmation')"
      :confirmText="t('confirm')"
      :cancelText="t('cancel')"
    />
  </v-container>
</template>

<style scoped>
</style>
